import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A Ribbon Chart is a data visualization tool used to display the relative sizes and changes
          in multiple categories over time or across different dimensions. It consists of colored
          &rdquo;ribbons&rdquo; that represent each category, with their width indicating the
          magnitude of the value. Ribbon charts are effective in illustrating trends, transitions,
          and comparisons between categories, particularly when tracking changes in proportions or
          rankings over time. They are especially useful for visualizing data with multiple
          dimensions, such as market share fluctuations, resource allocation, or categorical
          performance metrics.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Ribbon charts are a compelling visualization tool for analyzing dynamic changes across
          categories. Here are their three most significant applications:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Tracking Category Rankings Over Time:</strong> Ribbon charts display how the
            ranking of categories evolves, providing a clear view of shifting trends or priorities
            over a specified period.
          </li>
          <li>
            <strong>Visualizing Proportional Changes:</strong> The varying widths of ribbons
            represent the proportional size or influence of categories, enabling easy comparison of
            their significance.
          </li>
          <li>
            <strong>Highlighting Transitions and Trends:</strong> Ribbon charts emphasize smooth
            transitions between time points or events, making them ideal for showcasing sequential
            data patterns.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Ribbon charts are powerful tools for visualizing trends over time with stacked values.
          However, they come with a few notable limitations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Complexity in Interpretation:</strong> The stacked nature of ribbon charts can
            make it challenging to compare individual categories accurately, particularly if the
            categories have similar values. This complexity increases as the number of categories
            grows, potentially overwhelming the viewer.
          </li>
          <li>
            <strong>Limited Use for Static Comparisons:</strong> Ribbon charts are designed to
            display trends and changes over time. They are less effective for datasets requiring
            detailed static comparisons since the stacked areas can obscure individual category
            contributions.
          </li>
          <li>
            <strong>Susceptibility to Misleading Visuals:</strong> Variations in ribbon height can
            be visually misleading if the chart isn&apos;t scaled properly. Minor differences may
            appear exaggerated or understated, potentially leading to incorrect interpretations of
            the data.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          In Ribbon charts, color selection plays a crucial role in visually distinguishing
          different categories and the flow of data over time. Using distinct and contrasting colors
          helps to clearly differentiate the ribbons, making it easier to identify trends and
          transitions. Gradients or color variations within a ribbon can represent the magnitude of
          change or intensity of a particular category. When multiple categories are displayed,
          careful use of color can guide the viewer&apos;s attention to key areas of interest,
          improving both the aesthetic appeal and the readability of the chart. Consistency in color
          usage enhances understanding of how values relate and change across time.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'sequential']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Ribbon charts are visually appealing and useful for showing cumulative trends, but they
          come with specific challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Complexity in Reading Overlaps:</strong> When multiple ribbons overlap,
            especially with similar colors, it can be challenging to discern individual
            contributions and trends.
          </li>
          <li>
            <strong>Difficulty in Data Scaling:</strong> Large variations in data values can lead to
            ribbons that dominate the chart, overshadowing smaller, yet potentially significant,
            contributions.
          </li>
          <li>
            <strong>Limited Comparability:</strong> Ribbon charts excel at showing progression but
            can make direct comparisons between individual categories or points less intuitive.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
